import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient } from '@wix/sdk-types';
import * as ambassadorWixCalendarV3EventsView from './calendar-v3-events-view-event-views.http.js';

/**
 * The `eventsView` object gives you details about how far into the future you can
 * easily get a complete picture of a calendar. Subscribing to the event view
 * webhooks lets you keep track of a calendar's single-occurrence, `EXCEPTION`, and
 * `INSTANCE` events at least one year into the future.
 */
export interface EventsView {
  /**
   * End date of the event view in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  endDate?: Date | null;
  /**
   * Number of days from now until the event view ends.
   * @readonly
   */
  futureDurationInDays?: number | null;
}

export interface EventsViewProjectionUpdated
  extends EventsViewProjectionUpdatedTypeOneOf {
  /**
   * Available when there is a change to an event in the current event view, including when:
   * + An event is created within the view.
   * + An existing event is updated, unless its start time moves outside the view. Then, `eventRemoved` is returned.
   * + An event previously outside the view is rescheduled to fall within it.
   * + The view is extended, bringing an event into scope.
   */
  eventAddedOrUpdated?: EventAddedOrUpdated;
  /**
   * Available when an event has been removed from the current view, including
   * when:
   * + An event within the view is deleted.
   * + An event within the view is rescheduled so that its start time falls outside the view.
   */
  eventRemoved?: EventRemoved;
  /**
   * ID of the app that owns the event. Identical to `appId` of the
   * [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction)
   * to which the event belongs.
   * @format GUID
   */
  appId?: string | null;
}

/** @oneof */
export interface EventsViewProjectionUpdatedTypeOneOf {
  /**
   * Available when there is a change to an event in the current event view, including when:
   * + An event is created within the view.
   * + An existing event is updated, unless its start time moves outside the view. Then, `eventRemoved` is returned.
   * + An event previously outside the view is rescheduled to fall within it.
   * + The view is extended, bringing an event into scope.
   */
  eventAddedOrUpdated?: EventAddedOrUpdated;
  /**
   * Available when an event has been removed from the current view, including
   * when:
   * + An event within the view is deleted.
   * + An event within the view is rescheduled so that its start time falls outside the view.
   */
  eventRemoved?: EventRemoved;
}

export interface EventAddedOrUpdated {
  /** Event that was added or updated. */
  event?: Event;
  /**
   * Event’s original version before an update. Available only when an event is
   * updated. Not available when a new event is created or an existing event falls
   * into the view because the view is extended.
   */
  previousEvent?: Event;
}

/**
 * An event is a scheduled entry on a calendar that includes details like timing,
 * location, and participants. Each event is associated with
 * a [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),
 * from which it may [inherit default values](https://dev.wix.com/docs/rest/business-management/calendar/default-values).
 * Events can either be standalone, part of a recurring series, or define a
 * recurrence pattern. You can also specify whether events block time on the
 * schedule or allow other events to be scheduled concurrently.
 */
export interface Event {
  /**
   * Event ID.
   * @minLength 36
   * @maxLength 250
   * @readonly
   */
  _id?: string | null;
  /**
   * ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)
   * the event belongs to.
   * After creating an event, you can't assign it to a different schedule.
   * @format GUID
   * @immutable
   */
  scheduleId?: string | null;
  /**
   * ID of the external schedule the event belongs to.
   *
   * For example, if the event belongs to a Bookings staff member, identical to
   * the ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).
   * If the schedule belongs to a Bookings service,
   * identical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).
   * @format GUID
   * @readonly
   */
  externalScheduleId?: string | null;
  /**
   * Schedule name.
   * @readonly
   */
  scheduleName?: string | null;
  /**
   * Event type. You can set the event type but you can't update it.
   *
   * Supported values:
   * + `DEFAULT`: A standard event that's not further specified.
   * + `WORKING_HOURS`: The event adds working hours to a schedule.
   * By default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).
   * To retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.
   * Additional supported values, if Wix Bookings is installed:
   * + `APPOINTMENT`: Bookings appointment event.
   * + `CLASS`: Bookings class event.
   * + `COURSE`: Bookings course event.
   * @immutable
   */
  type?: string | null;
  /**
   * Event status.
   *
   * Supported values:
   * + `CONFIRMED`: Event is scheduled to happen or has happened.
   * + `CANCELLED`: Event has been canceled.
   *
   * Default: `CONFIRMED`
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Event title.
   *
   * Min: 1 character
   * Max: 200 characters
   * @minLength 1
   * @maxLength 200
   */
  title?: string | null;
  /** Information about when the event starts. */
  start?: ZonedDate;
  /**
   * Information about when the event ends.
   *
   * Maximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first).
   */
  end?: ZonedDate;
  /**
   * Information about when the event starts adjusted to the `timeZone` of the
   * business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedStart?: ZonedDate;
  /**
   * Information about when the event ends adjusted to the `timeZone` of the
   * business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedEnd?: ZonedDate;
  /**
   * Time zone the event is associated with in
   * [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * Only regional time zones and UTC are supported.
   * For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).
   */
  timeZone?: string | null;
  /**
   * Information about whether the event is a single occurrence or part of a
   * recurring series.
   *
   * Supported values:
   * + `NONE`: The event occurs only once and doesn't repeat.
   * + `MASTER`: Defines the recurrence pattern for a series of recurring events.
   * + `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{"recurrenceType": "INSTANCE"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.
   * + `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.
   *
   * Default: `NONE`
   * @readonly
   */
  recurrenceType?: RecurrenceTypeWithLiterals;
  /**
   * Recurrence pattern for a series of events. This field is required when
   * creating a `MASTER` event and isn't available for non-recurring events.
   * You can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.
   *
   * For example, an event that repeats every second Monday until January 7, 2026,
   * at 8:00 AM has the following `recurrenceRule`:
   * - `frequency = WEEKLY`
   * - `interval = 2`
   * - `days = [MONDAY]`
   * - `until = 20260107T08:00:00Z`
   */
  recurrenceRule?: RecurrenceRule;
  /**
   * ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`
   * and `EXCEPTION` events.
   * @minLength 64
   * @maxLength 64
   * @readonly
   */
  recurringEventId?: string | null;
  /**
   * Specifies whether the event blocks time in the schedule it belongs to.
   *
   * Supported values:
   * + `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.
   * + `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.
   *
   * Default: `OPAQUE`
   */
  transparency?: TransparencyWithLiterals;
  /**
   * Event location.
   *
   * Default: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to
   */
  location?: Location;
  /**
   * List of [Wix Bookings resources](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object)
   * affected by the event. This could include, for example, the
   * [Wix Bookings staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/staff-member-v1/staff-member-object)
   * providing the service or the room where the appointment takes place.
   *
   * Max: 100 resources
   * @maxSize 100
   */
  resources?: Resource[];
  /**
   * Maximum number of participants who can participate in the event.
   *
   * Default: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to
   */
  totalCapacity?: number | null;
  /**
   * Number of participants who can still be added to the event.
   * @readonly
   */
  remainingCapacity?: number | null;
  /**
   * Information about the event's participants.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).
   * @readonly
   */
  participants?: Participants;
  /**
   * Information about the event's online conferencing.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).
   *
   * Default: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to
   */
  conferencingDetails?: ConferencingDetails;
  /**
   * Additional notes about the event.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).
   *
   * Min: 1 character
   * Max: 5000 characters
   * @minLength 1
   * @maxLength 5000
   */
  notes?: string | null;
  /**
   * List of fields whose values are inherited.
   * + For single-occurrence events, values are inherited from the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).
   * + For `INSTANCE` and `EXCEPTION` events, values are inherited from the related `MASTER` event.
   * @readonly
   */
  inheritedFields?: FieldWithLiterals[];
  /**
   * ID of the app that owns the event. Identical to `appId` of the
   * [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction)
   * to which the event belongs.
   * @format GUID
   * @readonly
   */
  appId?: string | null;
  /**
   * List of permissions associated with the event. Refer to the
   * [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)
   * for more information.
   * @readonly
   */
  permissions?: Permission[];
  /** Extensions enabling applications or users to save custom data related to the event. */
  extendedFields?: ExtendedFields;
  /**
   * Revision number, which increments each time the event is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the event.
   * Ignored when creating an event.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export enum Status {
  /** Event is scheduled to happen or has happened. */
  CONFIRMED = 'CONFIRMED',
  /** Event has been canceled. */
  CANCELLED = 'CANCELLED',
}

/** @enumType */
export type StatusWithLiterals = Status | 'CONFIRMED' | 'CANCELLED';

/** A date time with a time zone, having the UTC offset and date determined by the server. */
export interface ZonedDate {
  /**
   * Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   * Wix Calendar APIs ignore seconds.
   * @format LOCAL_DATE_TIME
   */
  localDate?: string | null;
  /**
   * Time zone in
   * [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * For example, `America/New_York`.
   * @readonly
   */
  timeZone?: string | null;
  /**
   * UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   * Not available for adjusted date fields.
   * @readonly
   */
  utcDate?: Date | null;
}

export enum RecurrenceType {
  /** The event occurs only once and doesn't repeat. */
  NONE = 'NONE',
  /** Defines the recurrence pattern for a series of recurring events. */
  MASTER = 'MASTER',
  /** A specific occurrence of a recurring event. You can't create an event with `{"recurrenceType": "INSTANCE"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`. */
  INSTANCE = 'INSTANCE',
  /** A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event. */
  EXCEPTION = 'EXCEPTION',
}

/** @enumType */
export type RecurrenceTypeWithLiterals =
  | RecurrenceType
  | 'NONE'
  | 'MASTER'
  | 'INSTANCE'
  | 'EXCEPTION';

export interface RecurrenceRule {
  /**
   * Frequency how often the event repeats. Works together with `interval`.
   *
   * Supported values:
   * + `WEEKLY`: The event's recurrence pattern is based on weeks.
   */
  frequency?: FrequencyWithLiterals;
  /**
   * Interval how often the event repeats. Works together with `frequency`.
   * For example, `frequency` set to `WEEKLY`  and `interval` set to `2` means
   * the event repeats every 2 weeks.
   *
   * Min: `1`
   * Max: `4`
   * Default: `1`
   * @min 1
   * @max 4
   */
  interval?: number | null;
  /**
   * Days of the week when the recurring event takes place.
   * Currently, only a single day is supported.
   *
   * Min: 1 day
   * Max: 1 day
   * @minSize 1
   * @maxSize 1
   */
  days?: DayWithLiterals[];
  /**
   * Date until when the event repeats.
   * If not specified, the event repeats forever.
   */
  until?: ZonedDate;
  /**
   * Date until when the event repeats
   * adjusted to the `timeZone` of the business
   * [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedUntil?: ZonedDate;
}

export enum Frequency {
  UNKNOWN_FREQUENCY = 'UNKNOWN_FREQUENCY',
  /** The event's recurrence pattern is based on weeks. */
  WEEKLY = 'WEEKLY',
}

/** @enumType */
export type FrequencyWithLiterals = Frequency | 'UNKNOWN_FREQUENCY' | 'WEEKLY';

export enum Day {
  /** The recurring event takes place on Mondays. */
  MONDAY = 'MONDAY',
  /** The recurring event takes place on Tuesdays. */
  TUESDAY = 'TUESDAY',
  /** The recurring event takes place on Wednesday. */
  WEDNESDAY = 'WEDNESDAY',
  /** The recurring event takes place on Thursdays. */
  THURSDAY = 'THURSDAY',
  /** The recurring event takes place on Fridays. */
  FRIDAY = 'FRIDAY',
  /** The recurring event takes place on Saturdays. */
  SATURDAY = 'SATURDAY',
  /** The recurring event takes place on Sundays. */
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayWithLiterals =
  | Day
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export enum Transparency {
  UNKNOWN_TRANSPARENCY = 'UNKNOWN_TRANSPARENCY',
  /** The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time. */
  OPAQUE = 'OPAQUE',
  /** The schedule remains open during the event, allowing other events to be scheduled concurrently. */
  TRANSPARENT = 'TRANSPARENT',
}

/** @enumType */
export type TransparencyWithLiterals =
  | Transparency
  | 'UNKNOWN_TRANSPARENCY'
  | 'OPAQUE'
  | 'TRANSPARENT';

export interface Location {
  /**
   * [Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)
   * ID. Available only for `BUSINESS` locations.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Location type.
   *
   * Supported values:
   * + `BUSINESS`: The event is held at a business location.
   * + `CUSTOMER`: The event is held at the customer's location, such as their home or office.
   * + `CUSTOM`: The event is held at an address or venue not tied to the business or customer.
   */
  type?: LocationTypeWithLiterals;
  /**
   * Location name. For `BUSINESS` locations, it's identical to the
   * [location](https://dev.wix.com/docs/rest/business-management/locations/location-object)
   * `name`.
   *
   * Min: 1 character
   * Max: 150 characters
   * @minLength 1
   * @maxLength 150
   */
  name?: string | null;
  /**
   * Location address. For `BUSINESS` locations, it's identical to the
   * [location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s
   * `formattedAddress`.
   * @minLength 1
   * @maxLength 300
   */
  address?: string | null;
}

export enum LocationType {
  /** The event is held at a business location. */
  BUSINESS = 'BUSINESS',
  /** The event is held at the customer's location, such as their home or office. */
  CUSTOMER = 'CUSTOMER',
  /** The event is held at an address or venue not tied to the business or customer. */
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type LocationTypeWithLiterals =
  | LocationType
  | 'BUSINESS'
  | 'CUSTOMER'
  | 'CUSTOM';

export interface Resource {
  /**
   * Resource ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Resource name.
   * @readonly
   */
  name?: string | null;
  /**
   * Resource type.
   * @readonly
   */
  type?: string | null;
  /**
   * Specifies whether the event blocks time in the resource's schedule.
   *
   * Supported values:
   * + `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.
   * + `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.
   *
   * Default: `OPAQUE`.
   */
  transparency?: TransparencyWithLiterals;
  /**
   * Permission role associated with the resource. Refer to the
   * [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)
   * for more information.
   */
  permissionRole?: RoleWithLiterals;
}

export enum Role {
  /** Full read and write access. */
  WRITER = 'WRITER',
  /** Full read access, write access limited to `event.notes`. */
  COMMENTER = 'COMMENTER',
}

/** @enumType */
export type RoleWithLiterals = Role | 'WRITER' | 'COMMENTER';

export interface Participants {
  /**
   * Total number of participants.
   *
   * Min: `0`
   */
  total?: number | null;
  /**
   * Full or partial list of the participants.
   *
   * Max: 50 participants
   * @maxSize 50
   */
  list?: Participant[];
  /**
   * Whether there are more participants for the event than listed.
   *
   * + `false`: The list includes all participants.
   * + `true`: The list doesn't include all participants.
   */
  hasMore?: boolean | null;
  /**
   * Participant's status.
   *
   * Supported values:
   * + `CONFIRMED`: All participants are confirmed.
   * + `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet.
   */
  status?: ParticipantsStatusWithLiterals;
}

export interface Participant {
  /**
   * Participant name.
   *
   * Min: 1 character
   * Max: 200 characters
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /**
   * Participant's phone number.
   * @format PHONE
   */
  phone?: string | null;
  /**
   * Participant's email address.
   * @format EMAIL
   */
  email?: string | null;
  /**
   * [Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)
   * ID of the participant.
   * @format GUID
   */
  contactId?: string | null;
}

export enum ParticipantsStatus {
  /** All participants are confirmed. */
  CONFIRMED = 'CONFIRMED',
  /** At least one participant isn't confirmed yet. */
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
}

/** @enumType */
export type ParticipantsStatusWithLiterals =
  | ParticipantsStatus
  | 'CONFIRMED'
  | 'PENDING_CONFIRMATION';

export interface ConferencingDetails {
  /** Conference type. For example, `Zoom`. */
  type?: TypeWithLiterals;
  /**
   * URL used by the host to start the conference.
   *
   * Min: 1 character
   * Max: 2000 characters
   * @minLength 1
   * @maxLength 2000
   */
  hostUrl?: string | null;
  /**
   * URL used by a guest to join the conference.
   *
   * Min: 1 character
   * Max: 2000 characters
   * @minLength 1
   * @maxLength 2000
   */
  guestUrl?: string | null;
  /**
   * Conference password.
   *
   * Min: 1 character
   * Max: 100 characters
   * @minLength 1
   * @maxLength 100
   */
  password?: string | null;
  /**
   * Conference ID in an external system.
   *
   * Min: 1 character
   * Max: 150 characters
   * @minLength 1
   * @maxLength 150
   */
  externalId?: string | null;
  /**
   * Conferencing provider information.
   * @internal
   */
  provider?: Provider;
}

export enum ProviderType {
  /** Custom conferencing link manually configured by the user. */
  CUSTOM = 'CUSTOM',
  /** Online meeting provider integrated via an app. */
  ONLINE_MEETING = 'ONLINE_MEETING',
}

/** @enumType */
export type ProviderTypeWithLiterals =
  | ProviderType
  | 'CUSTOM'
  | 'ONLINE_MEETING';

export interface OnlineMeetingInfo {
  /** @format GUID */
  appId?: string;
}

export enum Type {
  UNKNOWN_TYPE = 'UNKNOWN_TYPE',
  ZOOM = 'ZOOM',
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNKNOWN_TYPE' | 'ZOOM' | 'CUSTOM';

export interface Provider extends ProviderProviderInfoOneOf {
  /** Information about online meeting providers. */
  onlineMeetingInfo?: OnlineMeetingInfo;
  providerType?: ProviderTypeWithLiterals;
}

/** @oneof */
export interface ProviderProviderInfoOneOf {
  /** Information about online meeting providers. */
  onlineMeetingInfo?: OnlineMeetingInfo;
}

export enum Field {
  /** `title` is inherited from the schedule or `MASTER` event. */
  TITLE = 'TITLE',
  /** `timeZone` is inherited from the schedule or `MASTER` event. */
  TIME_ZONE = 'TIME_ZONE',
  /** `start` and `end` are inherited from the `MASTER` event. */
  TIME = 'TIME',
  /** `location` is inherited from the schedule or `MASTER` event. */
  LOCATION = 'LOCATION',
  /** `resources` is inherited from the `MASTER` event. */
  RESOURCES = 'RESOURCES',
  /** `capacity` is inherited from the schedule or `MASTER` event. */
  CAPACITY = 'CAPACITY',
  /** `participants` is inherited from the `MASTER` event. */
  PARTICIPANTS = 'PARTICIPANTS',
  /** `conferencingDetails` is inherited from the schedule or `MASTER` event. */
  CONFERENCING_DETAILS = 'CONFERENCING_DETAILS',
}

/** @enumType */
export type FieldWithLiterals =
  | Field
  | 'TITLE'
  | 'TIME_ZONE'
  | 'TIME'
  | 'LOCATION'
  | 'RESOURCES'
  | 'CAPACITY'
  | 'PARTICIPANTS'
  | 'CONFERENCING_DETAILS';

export interface Permission {
  /**
   * [Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)
   * granted the permission.
   */
  identity?: CommonIdentificationData;
  /**
   * Permission role.
   *
   * Supported values:
   * + `WRITER`: Full read and write access.
   * + `COMMENTER`: Full read access, write access limited to `event.notes`.
   */
  role?: RoleWithLiterals;
}

export interface CommonIdentificationData
  extends CommonIdentificationDataIdOneOf {
  /**
   * ID of a Wix user. For example, the site owner or a
   * [site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).
   * @format GUID
   */
  wixUserId?: string;
}

/** @oneof */
export interface CommonIdentificationDataIdOneOf {
  /**
   * ID of a Wix user. For example, the site owner or a
   * [site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).
   * @format GUID
   */
  wixUserId?: string;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  WIX_USER = 'WIX_USER',
}

/** @enumType */
export type IdentityTypeWithLiterals = IdentityType | 'UNKNOWN' | 'WIX_USER';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface EventRemoved {
  /** Event that was removed from the current view. */
  event?: Event;
}

export interface EventsViewExtended {
  /**
   * Extended date of the events view in `YYYY-MM-DDTHH:MM:SSZ`
   * format.
   */
  extendedEndDate?: Date | null;
}

export interface GetEventsViewRequest {}

export interface GetEventsViewResponse {
  /** Retrieved event view. */
  eventsView?: EventsView;
}

export interface ExtendEventsViewRequest {
  /**
   * Number of days from now until the event view ends.
   * Must be greater than the current value.
   */
  futureDurationInDays?: number | null;
}

export interface ExtendEventsViewResponse {
  /** Udated event view. */
  eventsView?: EventsView;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface EventsViewExtendedEnvelope {
  data: EventsViewExtended;
  metadata: EventMetadata;
}

/**
 * Triggered when the current event view is extended.
 *
 *
 * Doesn’t include details about existing events added to the view due to the
 * extension. To receive updates about those events, subscribe to
 * [Events View Projection Updated](https://dev.wix.com/docs/rest/business-management/calendar/event-views-v3/events-view-projection-updated).
 *
 * The event view is periodically extended by Wix Calendars, you can't manually
 * extend it.
 * @permissionScope Read Calendar - Including PI
 * @permissionScopeId SCOPE.DC-CALENDAR.READ-PI
 * @permissionScope Manage Calendars
 * @permissionScopeId SCOPE.DC-CALENDAR.MANAGE
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Read bookings calendar - including participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR-WITH-PARTICIPANTS
 * @permissionId CALENDAR.EVENT_VIEW_READ
 * @webhook
 * @eventType wix.calendar.v3.events_view_extended
 * @slug extended
 */
export declare function onEventsViewExtended(
  handler: (event: EventsViewExtendedEnvelope) => void | Promise<void>
): void;

export interface EventsViewProjectionUpdatedEnvelope {
  data: EventsViewProjectionUpdated;
  metadata: EventMetadata;
}

/**
 * Triggered when an [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)
 * is added to, removed from, or updated within the current event view. This includes cases when:
 * + A new event is created within the view.
 * + An existing event within the view is updated, including when it's rescheduled to start outside the view.
 * + An event within the view is deleted.
 * + An event previously outside the view is rescheduled to fall within it.
 * + The view is extended, bringing an existing event into scope.
 * @permissionScope Read Calendar - Including PI
 * @permissionScopeId SCOPE.DC-CALENDAR.READ-PI
 * @permissionScope Manage Calendars
 * @permissionScopeId SCOPE.DC-CALENDAR.MANAGE
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Read bookings calendar - including participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR-WITH-PARTICIPANTS
 * @permissionId CALENDAR.EVENT_READ_PI
 * @webhook
 * @eventType wix.calendar.v3.events_view_projection_updated
 * @slug projection_updated
 */
export declare function onEventsViewProjectionUpdated(
  handler: (event: EventsViewProjectionUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Doesn’t return details about events within the view. You can use
 * [queryEvents()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/query-events),
 * providing `eventsView.endDate` as `toLocalDate` to retrieve the
 * events.
 * @public
 * @permissionId CALENDAR.EVENT_VIEW_READ
 * @applicableIdentity APP
 * @fqn wix.calendar.eventviews.v3.EventViewsService.GetEventsView
 */
export async function getEventsView(): Promise<GetEventsViewResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixCalendarV3EventsView.getEventsView(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
